import {
    ComponentRef, Injectable, TemplateRef,
    EventEmitter, Renderer2, RendererFactory2, NgZone
} from '@angular/core';


import { ModalBackdropComponent } from './modal-backdrop.component';
import { ModalContainerComponent } from './modal-container.component';
import {
    CLASS_NAME,
    modalConfigDefaults,
    ModalOptions,
    TRANSITION_DURATIONS
} from './modal-options.class';
import { BsModalRef } from './bs-modal-ref.service';
import { Utils } from '@farris/ui-modal/utils';
import { ComponentLoaderFactory, ComponentLoader } from '@farris/ui-modal/component-loader';
import { CommonUtils } from '@farris/ui-common';

@Injectable()
export class BsModalService {
    version = '0.0.6';
    config: ModalOptions = modalConfigDefaults;

    onShow: EventEmitter<any> = new EventEmitter();
    onShown: EventEmitter<any> = new EventEmitter();
    onHide: EventEmitter<any> = new EventEmitter();
    onHidden: EventEmitter<any> = new EventEmitter();

    protected isBodyOverflowing = false;
    protected originalBodyPadding = 0;

    protected scrollbarWidth = 0;

    protected backdropRef: ComponentRef<ModalBackdropComponent>;
    private _backdropLoader: ComponentLoader<ModalBackdropComponent>;
    // 标记模态和非模态窗口的总个数
    private modalsCount = 0;
    private lastDismissReason = '';

    private loaders: ComponentLoader<ModalContainerComponent>[] = [];

    private _renderer: Renderer2;

    private _documentMouseDownHandler = null;
    private commonUtils: CommonUtils = null;
    // 标记非模态窗口个数
    private modellessCount=0;

    constructor(rendererFactory: RendererFactory2, private clf: ComponentLoaderFactory, public ngZone: NgZone) {

        this._renderer = rendererFactory.createRenderer(null, null);

        this._backdropLoader = this.clf.createLoader<ModalBackdropComponent>(
            null,
            null,
            this._renderer
        );

        this.commonUtils = new CommonUtils();
    }

    private clearDocumentEvents() {
        if (this._documentMouseDownHandler) {
            document.body.removeEventListener('mousedown', this._documentMouseDownHandler);
        }
        this._documentMouseDownHandler = null;
    }

    /** Shows a modal */
    show(content: string | TemplateRef<any> | any, config?: ModalOptions): BsModalRef {
        this.modalsCount++;
        this._createLoaders();
        this.config = Object.assign({}, modalConfigDefaults, config);

        this.checkDialogSize();
        if(this.isModeless()){            
            this.modellessCount++;
        } else{
            this._showBackdrop();
        }      
        this.lastDismissReason = null;

        const modal = this._showModal(content);
        return modal;
    }
    private isModeless(){
        if(this.config.hasOwnProperty('modeless')){
            let tResult=this.config['modeless'];
            if(typeof tResult =='string'){
                // 解决在设计器上传递参数是字符串的问题
                return tResult=='true';
            }
            return tResult;
        }
        return false;
    }

    hide(level: number) {
        if (this.modalsCount-this.modellessCount === 1) {
            this._hideBackdrop();
            this.resetScrollbar();
        }
        this.modalsCount = this.modalsCount >= 1 ? this.modalsCount - 1 : 0;
        // this.ngZone.runOutsideAngular(() => {
        //     setTimeout(() => {
            // this._hideModal(level);
            // this.removeLoaders(level);

        //     }, this.config.animated ? TRANSITION_DURATIONS.BACKDROP : 0);
        // });
        this._hideModal(level);
        this.removeLoaders(level);
        if(this.modalsCount==0){
            this.clearDocumentEvents();
        }        
    }

    _showBackdrop(): void {
        const isBackdropEnabled =
            this.config.backdrop || this.config.backdrop === 'static';
        const isBackdropInDOM =
            !this.backdropRef || !this.backdropRef.instance.isShown;
        /**
         * 此处做限定是解决【弹出多个模态窗口时，因为遮罩层的叠加，导致背景很黑】的问题
         */
        if (this.modalsCount-this.modellessCount === 1) {
            this.removeBackdrop();

            if (isBackdropEnabled && isBackdropInDOM) {
                this._backdropLoader
                    .attach(ModalBackdropComponent)
                    .to('body')
                    .show({ isAnimated: this.config.animated });
                this.backdropRef = this._backdropLoader._componentRef;
            }
        }
    }

    _hideBackdrop(): void {
        if (!this.backdropRef) {
            return;
        }
        this.backdropRef.instance.isShown = false;
        const duration = this.config.animated ? TRANSITION_DURATIONS.BACKDROP : 0;
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => this.removeBackdrop(), duration);
        });
    }

    private updateZindex(_modal) {
        const maxZindex = this.commonUtils.getFloatingLayerIndex();
        // 非模态下zIndex的设置
        _modal.dialog.instance.updateDialogZindex(maxZindex);
        _modal.dialog.location.nativeElement.style.zIndex = maxZindex;
    }

    _showModal(content: any): BsModalRef {
        const modalLoader = this.loaders[this.loaders.length - 1];
        const bsModalRef = new BsModalRef();

        const modalContainerRef = modalLoader
            .provide({ provide: ModalOptions, useValue: this.config })
            .provide({ provide: BsModalRef, useValue: bsModalRef })
            .attach(ModalContainerComponent)
            .to('body')
            .show({ content, isAnimated: this.config.animated,
                initialState: this.config.initialState,
                bsModalService: this
            });
        modalContainerRef.instance.level = this.getModalsCount();
        modalContainerRef.instance.dialogType = this.config.dialogType;
        modalContainerRef.instance.iframe = '';
        if (this.config.dialogType === 'iframe') {
            modalContainerRef.instance.iframe = content;
        }
        bsModalRef.close = (e) => {
            modalContainerRef.instance.close(e);
            this.modalsCount==0&&this.clearDocumentEvents();
        };


        // 如果是消息类型的，出现滚动条就不响应动画了
        if (!this.config.fitContent) {
            modalContainerRef.instance.toCenter();
            this.ngZone.runOutsideAngular(() => {
                document.body.addEventListener('mousedown', this._documentMouseDownHandler = (e) => {this._onMouseDown(e,modalContainerRef.instance.isModeless)});
            });
        }
        bsModalRef.content = modalLoader.getInnerComponent() || null;

        bsModalRef.dialog = modalContainerRef;
        bsModalRef.buttons = modalContainerRef.instance.buttons;

        modalContainerRef.instance['modalRef'] = bsModalRef;

        if (this.config.opened) {
            this.config.opened({ instance: bsModalRef });
        }

        this.updateZindex(bsModalRef);

        return bsModalRef;
    }
    /**
     * 增加参数，解决，在当前模态窗口内先弹出非模态再弹出Dialog时，点击外层
     * @param e 
     * @param curModalisModeless 
     * @returns 
     */
    _onMouseDown(e: any,curModalisModeless=null) {
        if(curModalisModeless){
            var modalDialogEl=e.target.classList.contains('modal-dialog')?e.target:null;
            modalDialogEl=modalDialogEl?modalDialogEl:e.target.closest('.modal-dialog');
            if(modalDialogEl){
                const maxZindex = this.commonUtils.getFloatingLayerIndex();
                modalDialogEl.style.zIndex = maxZindex;
                modalDialogEl.parentElement.style.zIndex = maxZindex;
            }
            return false;
        }else{
            if (e.target.localName === 'modal-container' || e.target.classList.contains('farris-modal')) {
          
                this._renderer.addClass(e.target, 'animated');
                this._renderer.addClass(e.target, 'shake');
                this.ngZone.runOutsideAngular(() => {
                    setTimeout(() => {
                        this._renderer.removeClass(e.target, 'animated');
                        this._renderer.removeClass(e.target, 'shake');
                    }, 650);
                });
                return false;
            }
           
        }        
    }

    _hideModal(level: number): void {
        const modalLoader = this.loaders[level - 1];
        if (modalLoader) {
            modalLoader.hide();
        }
    }

    getModalsCount(): number {
        return this.modalsCount;
    }

    getCurrentModalContainer() {
        return this.loaders[this.getModalsCount() - 1];
    }

    setDismissReason(reason: string) {
        this.lastDismissReason = reason;
    }

    removeBackdrop(): void {
        this._backdropLoader.hide();
        this.backdropRef = null;
    }

    /** AFTER PR MERGE MODAL.COMPONENT WILL BE USING THIS CODE */
    /** Scroll bar tricks */
    /** @internal */
    checkScrollbar(): void {
        this.isBodyOverflowing = document.body.clientWidth < window.innerWidth;
        this.scrollbarWidth = this.getScrollbarWidth();
    }

    setScrollbar(): void {
        if (!document) {
            return;
        }

        this.originalBodyPadding = parseInt(
            window
                .getComputedStyle(document.body)
                .getPropertyValue('padding-right') || '0',
            10
        );

        if (this.isBodyOverflowing) {
            document.body.style.paddingRight = `${this.originalBodyPadding +
                this.scrollbarWidth}px`;
        }
    }

    private resetScrollbar(): void {
        document.body.style.paddingRight = `${this.originalBodyPadding}px`;
    }

    // thx d.walsh
    private getScrollbarWidth(): number {
        const scrollDiv = this._renderer.createElement('div');
        this._renderer.addClass(scrollDiv, CLASS_NAME.SCROLLBAR_MEASURER);
        this._renderer.appendChild(document.body, scrollDiv);
        const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        this._renderer.removeChild(document.body, scrollDiv);

        return scrollbarWidth;
    }

    private _createLoaders(): void {
        const loader = this.clf.createLoader<ModalContainerComponent>(
            null,
            null,
            this._renderer
        );
        this.copyEvent(loader.onBeforeShow, this.onShow);
        this.copyEvent(loader.onShown, this.onShown);
        this.copyEvent(loader.onBeforeHide, this.onHide);
        this.copyEvent(loader.onHidden, this.onHidden);
        this.loaders.push(loader);
    }

    private removeLoaders(level: number): void {
        this.loaders.splice(level - 1, 1);
        this.loaders.forEach(
            (loader: ComponentLoader<ModalContainerComponent>, i: number) => {
                loader.instance.level = i + 1;
            }
        );
    }

    private copyEvent(from: EventEmitter<any>, to: EventEmitter<any>) {
        from.subscribe(() => {
            to.emit(this.lastDismissReason);
        });
    }

    private checkDialogSize() {
        const newSize = Utils.checkDialogSize(this.config.width, this.config.height);
        if (this.config.width !== newSize.width) {
            this.config.width = newSize.width - 20;
        }

        if (this.config.height !== newSize.height) {
            this.config.height = newSize.height - 20;
        }
    }
    closeUpdateModelessCountByModal(curIsModeless){
        if(curIsModeless){
            this.modellessCount=this.modellessCount-1>0?this.modellessCount-1:0;
        }
    }
}
